import React from "react";
import { ShieldCheck } from "lucide-react";
import { APPLE_STORE_URL, GOOGLE_PLAY_URL } from "../lib/constants";

const AndroidIcon = ({ size = 30 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.523 15.34c-.6 0-1.086-.487-1.086-1.086 0-.6.487-1.087 1.086-1.087.6 0 1.087.487 1.087 1.087 0 .599-.487 1.086-1.087 1.086m-11.046 0c-.6 0-1.086-.487-1.086-1.086 0-.6.486-1.087 1.086-1.087.6 0 1.087.487 1.087 1.087 0 .599-.487 1.086-1.087 1.086m11.437-6.02l2.169-3.758a.451.451 0 0 0-.165-.616.451.451 0 0 0-.616.165l-2.196 3.803a13.63 13.63 0 0 0-5.106-.983c-1.812 0-3.532.35-5.106.983L4.699 5.111a.451.451 0 0 0-.616-.165.451.451 0 0 0-.165.616l2.17 3.758C2.4 11.183.5 14.192.5 17.5h23c0-3.308-1.9-6.317-5.586-8.18" />
  </svg>
);

const AppleIcon = ({ size = 30 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/**
 * DownloadStores — Bloco de destaque com dois botões grandes (Android + iOS)
 * Segue a referência visual: verde Android + roxo iOS, títulos, subtítulos
 * e selo de "Seguro, rápido e fácil de usar."
 *
 * variant:
 *  - "onLight" (default): fundo lavanda claro, títulos escuros
 *  - "onDark": para uso sobre banners escuros/gradientes (títulos brancos)
 */
export const DownloadStores = ({
  variant = "onLight",
  className = "",
  testId = "download-stores",
  compact = false,
}) => {
  const isDark = variant === "onDark";

  return (
    <div
      data-testid={testId}
      className={`relative rounded-3xl border ${
        isDark
          ? "bg-white/10 backdrop-blur-md border-white/25"
          : "bg-gradient-to-br from-white via-[#f5edff] to-white border-[#7c3aed]/12 shadow-[0_20px_50px_-25px_rgba(124,58,237,0.35)]"
      } p-6 sm:p-8 ${className}`}
    >
      {!compact && (
        <div className={`text-center ${isDark ? "text-white" : "text-[#2e1065]"}`}>
          <h3 className="font-display font-bold text-2xl sm:text-3xl leading-tight">
            Baixe agora e faça a diferença!
          </h3>
          <p
            className={`mt-2 text-sm sm:text-base ${
              isDark ? "text-white/80" : "text-[#3c1e6b]/70"
            }`}
          >
            Disponível gratuitamente para Android e iOS
          </p>
        </div>
      )}

      <div
        className={`grid sm:grid-cols-2 gap-4 sm:gap-5 ${
          compact ? "" : "mt-6"
        }`}
      >
        {/* Android */}
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-android-btn"
          className="group relative overflow-hidden rounded-2xl px-6 py-5 flex items-center gap-4 text-white font-semibold transition-all hover:-translate-y-1"
          style={{
            background:
              "linear-gradient(135deg, #7BC24A 0%, #5FA828 55%, #4E9020 100%)",
            boxShadow:
              "0 14px 30px -12px rgba(94, 170, 40, 0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
          }}
        >
          <div className="shrink-0 w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
            <AndroidIcon size={30} />
          </div>
          <div className="text-left leading-tight">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/85">
              Baixar para
            </div>
            <div className="font-display font-bold text-2xl">Android</div>
          </div>
        </a>

        {/* iOS */}
        <a
          href={APPLE_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-ios-btn"
          className="group relative overflow-hidden rounded-2xl px-6 py-5 flex items-center gap-4 text-white font-semibold transition-all hover:-translate-y-1"
          style={{
            background:
              "linear-gradient(135deg, #7c3aed 0%, #6d28d9 55%, #5B21B6 100%)",
            boxShadow:
              "0 14px 30px -12px rgba(124, 58, 237, 0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
          }}
        >
          <div className="shrink-0 w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
            <AppleIcon size={28} />
          </div>
          <div className="text-left leading-tight">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/85">
              Baixar para
            </div>
            <div className="font-display font-bold text-2xl">iPhone (iOS)</div>
          </div>
        </a>
      </div>

      {!compact && (
        <>
          <div
            className={`mt-4 grid sm:grid-cols-2 gap-2 text-center text-xs sm:text-sm ${
              isDark ? "text-white/75" : "text-[#3c1e6b]/60"
            } font-medium`}
          >
            <span data-testid="download-play-caption">
              Disponível no Google Play
            </span>
            <span data-testid="download-appstore-caption">
              Disponível na App Store
            </span>
          </div>

          <div
            className={`mt-5 pt-5 border-t ${
              isDark ? "border-white/15" : "border-[#7c3aed]/10"
            } flex items-center justify-center gap-2 text-sm ${
              isDark ? "text-white/85" : "text-[#3c1e6b]/70"
            } font-medium`}
          >
            <ShieldCheck
              size={16}
              className={isDark ? "text-[#7BC24A]" : "text-[#14B8A6]"}
            />
            Seguro, rápido e fácil de usar.
          </div>
        </>
      )}
    </div>
  );
};

export default DownloadStores;
